import { useEffect, useState } from 'react';

import { parseVariantKey, type Enchantment } from '@dca/shared';

import { api } from './api.ts';

export interface VariantLabel {
  hash: string;
  material: string;
  label: string;
  enchantments: Enchantment[];
  confirmations: number;
  confirmed: boolean;
  verified: boolean;
}

/**
 * Every known variant label, fetched once and shared.
 *
 * A hashed item can turn up on any page - your shop, a search result, a
 * restock list - and each one would otherwise need its own endpoint widened to
 * carry label data. One cached lookup by hash serves them all, and the hash is
 * globally unique so a label learned anywhere is correct everywhere.
 *
 * Deliberately module-level rather than a context: it is immutable reference
 * data, every caller wants the same copy, and threading a provider through the
 * tree buys nothing.
 */
let cache: Map<string, VariantLabel> | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function load(): void {
  if (cache || inflight) return;
  inflight = api.insights
    .variants()
    .then((result) => {
      cache = new Map(result.labels.map((label) => [label.hash, label as VariantLabel]));
    })
    .catch(() => {
      // A label is a nicety, not the page. Leave the cache empty and let items
      // render under their raw keys rather than failing the view.
      cache = new Map();
    })
    .finally(() => {
      inflight = null;
      for (const notify of listeners) notify();
    });
}

/** Records a label the user just submitted, so it shows without a refetch. */
export function rememberVariantLabel(label: VariantLabel): void {
  if (!cache) cache = new Map();
  cache.set(label.hash, label);
  for (const notify of listeners) notify();
}

export function useVariantLabels(): Map<string, VariantLabel> {
  const [, bump] = useState(0);

  useEffect(() => {
    const notify = () => bump((n) => n + 1);
    listeners.add(notify);
    load();
    return () => {
      listeners.delete(notify);
    };
  }, []);

  return cache ?? new Map();
}

/** The label for one item key, or null when it carries no hash or none is known. */
export function labelFor(
  labels: Map<string, VariantLabel>,
  itemKey: string,
): VariantLabel | null {
  const { hash } = parseVariantKey(itemKey);
  if (!hash) return null;
  return labels.get(hash) ?? null;
}
